/**
 * Living updates contract (F18, roadmap Phase 3): the per-page schedule and the
 * candidate studies a run has surfaced that wait for a person. Re-exported
 * from index.ts.
 *
 * Owner: living updates (F18). Shapes follow
 * specs/features/F18-living-updates/design.md, Interfaces, with the pause
 * reasons for billing, archiving and run preconditions added (LIVE-016).
 */
import { pagePath } from './paths';
import type { RunStatus } from './runs';

export const LIVING_ROUTES = {
  /**
   * GET -> 200 LivingScheduleResponse (E74; LIVE-016). PUT PutScheduleRequest -> 200 LivingSchedule (E44; LIVE-010 to LIVE-015,
   * LIVE-019). DELETE -> 204 (E75; LIVE-017).
   */
  livingSchedule: (slug: string) => `${pagePath(slug)}/schedule`,
  /** GET ?limit&cursor&state -> 200 CandidatesResponse (E76; LIVE-070, LIVE-072). */
  candidates: (slug: string) => `${pagePath(slug)}/candidates`,
} as const;

export type LivingCadence = 'weekly' | 'monthly' | 'quarterly';

/**
 * Why a schedule is paused (LIVE-016): an owner paused it, the last three scheduled runs failed (LIVE-027),
 * the workspace's trial or subscription ended or its AI-screening allowance is used up (F24), the review is
 * archived (BILL-092), or a run precondition no longer holds, such as an approved search strategy (LIVE-014).
 */
export type LivingPausedReason = 'user' | 'failures' | 'subscription' | 'plan_limit' | 'archived' | 'cannot_start';

export interface LivingSchedule {
  cadence: LivingCadence;
  paused: boolean;
  pausedReason: LivingPausedReason | null;
  /** The refusal's own words when the scheduler paused for billing or a precondition; null otherwise. */
  pausedDetail: string | null;
  /** Whole UTC hour; occurrences keep its time of day and day of month (LIVE-012). */
  anchorAt: string;
  /** Null while paused. */
  nextRunAt: string | null;
  consecutiveFailures: number;
  /** The page's latest run of any source, or null. */
  lastRun: { id: string; status: RunStatus; finishedAt: string | null } | null;
  updatedAt: string;
  /** 'user:<id>' or 'system'. */
  updatedBy: string;
}

/** GET LIVING_ROUTES.livingSchedule. */
export interface LivingScheduleResponse {
  schedule: LivingSchedule | null;
}

/** PUT LIVING_ROUTES.livingSchedule. Only these keys. */
export interface PutScheduleRequest {
  cadence?: LivingCadence;
  paused?: boolean;
  /** Required when a schedule exists: the updatedAt the owner last read (LIVE-019, REL-071). */
  updatedAt?: string;
}

/** details.reason of a 409 conflict from PUT LIVING_ROUTES.livingSchedule (LIVE-014, LIVE-019). */
export type ScheduleConflictReason = 'protocol_not_frozen' | 'not_pipeline_page' | 'no_approved_strategy' | 'stale_schedule';

/** LIVE-070: where a candidate waits, in the order a record moves through the review. */
export type CandidateState =
  | 'awaiting_screening'
  | 'awaiting_full_text'
  | 'awaiting_full_text_decision'
  | 'awaiting_extraction'
  | 'awaiting_confirmation';

export const CANDIDATE_STATES: readonly CandidateState[] = [
  'awaiting_screening',
  'awaiting_full_text',
  'awaiting_full_text_decision',
  'awaiting_extraction',
  'awaiting_confirmation',
];

/** A record or study a run surfaced that is not in the published analysis and not finally excluded (LIVE-070). */
export interface Candidate {
  /** Record id for kind 'record', study id for kind 'study'. */
  id: string;
  kind: 'record' | 'study';
  /** Record title, or the study's "Author Year" label. */
  label: string;
  state: CandidateState;
  /** The run whose search first found it, or null (an upload or an earlier import). */
  firstSeenRunId: string | null;
  firstSeenAt: string;
}

/** GET LIVING_ROUTES.candidates: counts over every candidate; the list is one page of them (LIVE-072). */
export interface CandidatesResponse {
  counts: Record<CandidateState, number>;
  candidates: Candidate[];
  nextCursor: string | null;
}
